/**
 * sdk文档 https://npm.gaoding.com/package/@gaoding/gd-sls-rum
 * sdk 数据格式 https://doc.huanleguang.com/pages/viewpage.action?pageId=286098878
 */
import { ENV } from "@/constants/envConfig";
import Cookies from "js-cookie";
import { filter } from "lodash";
import Router from "next/router";
import { isSpiderClient } from "./isSpider";
import { uidFieldName } from "./uid";

const version = process.env.NEXT_PUBLIC_VERSION;

function initGdSlsRum() {
  if (typeof window === "undefined") return;

  if (window.GD_SLS_RUM) {
    // 初始化配置
    window.GD_SLS_RUM.init({
      env: ENV,
      // userId: "", // 默认 getUserDeviceId()
      service: "huaban-pinit-front", // 项目名称,建议和 DevOps 平台的项目名一致，方便后续接入告警
      version,
      pluginConfig: {
        jsError: {
          errFilter(err) {
            // 过滤爬虫客户端渲染的错误
            if (isSpiderClient()) {
              return true;
            }

            // 过滤（本地打开文件）的错误
            if (!["http:", "https:"].includes(window.location.protocol)) {
              return true;
            }

            // 脚本注入检查，过滤第三方注入的脚本报错
            // https://doc.huanleguang.com/pages/viewpage.action?pageId=267074339
            if (
              decodeURIComponent(err["ex.file"] || "") === window.location.href
            ) {
              err["script"] = true;
              const scripts = document.querySelectorAll(
                "script:not(#__NEXT_DATA__,#baidu-tongji,[src],#CanaryClientInit)"
              );
              // 过滤谷歌插件的脚本注入
              const filterList = filter(
                scripts,
                (script) => !script.innerHTML.includes("googletagmanager")
              );

              if (filterList.length > 0) {
                return true;
              }
            }

            if (err && typeof err["ex.stacktrace"] === "string") {
              return (
                // 第三方插件错误排除
                err["ex.stacktrace"].includes("chrome-extension") ||
                err["ex.stacktrace"].includes("se-extension") ||
                err["ex.stacktrace"].includes("moz-extension") ||
                err["ex.stacktrace"].includes("safari-web-extension") ||
                err["ex.stacktrace"].includes("safari-extension") ||
                err["ex.stacktrace"].includes("[native code]") ||
                // 其他第三方错误排除
                err["ex.file"].includes("webkit-masked-url://") ||
                // 百度埋点脚本埋点错误排除
                err["ex.stacktrace"].includes("hm.baidu.com") ||
                // 非花瓣代码文件错误排除
                err["ex.stacktrace"].includes("remote.webview.preload.js") ||
                err["ex.stacktrace"].includes("widgets.min.js") ||
                // 错误分析文档： https://doc.huanleguang.com/display/~changkong/Cannot+read+property+%27getBoundingClientRect%27+of+undefined
                (err["ex.message"].includes("getBoundingClientRect") &&
                  err["page.user_agent"].includes("70.0.3538.25")) ||
                // 排除明显不是花瓣的错误
                err["ex.message"].includes("jsFinder") ||
                err["ex.message"].includes("KISSY") ||
                // nextjs 重复路由跳转错误，无法避免，且不影响页面正常使用，故过滤
                err["ex.message"].includes(
                  "attempted to hard navigate to the same URL"
                )
              );
            }
          },
        },
        resource: {
          errFilter(info) {
            // 过滤爬虫客户端渲染的错误
            if (isSpiderClient()) {
              return true;
            }

            // 过滤头像大小的图片失败请求，因为头像存在大量被删除的错误场景
            if (info?.["perf.name"].includes("sq75")) {
              return true;
            }

            if (typeof info !== "object") {
              return;
            }

            const xpath = info["resource.xpath"] || "";
            const url = info["resource.url"] || "";

            if (url.match(/(gaoding|huaban|dancf|hlgdata)/)) {
              // 过滤图片加载错误
              // 率叶插件导致的错误
              if (xpath.includes("lvye")) {
                return true;
              }
              // 未知第三方插件导致的错误
              if (xpath.includes(".docs-picture")) {
                return true;
              }
              // 未知第三方插件导致的错误
              return !!url.includes("layer/default/layer.css");
            }

            return true;
          },
        },
      },
      collectRouterTarget() {
        return Router?.pathname;
      },
    });

    setGdSlsRumUser();
    // 添加动态地址信息
    window.GD_SLS_RUM.trackPageTarget(window.__NEXT_DATA__?.page);
  }
}
// 提前执行监控初始化
initGdSlsRum();

export default initGdSlsRum;

export function setGdSlsRumUser(user) {
  if (typeof window === "undefined") return;
  if (window.GD_SLS_RUM) {
    window.GD_SLS_RUM.setOptions({
      userId: user?.user_id || Cookies.get(uidFieldName) || "",
    });
  }
}

/**
 * 错误日志上报
 * @param {Error|string|number} err
 * @param {Record<string, any>} [customLog] 自定义日志信息(可选)
 * @returns
 */
export function gdSlsReportError(err, customLog) {
  try {
    if (typeof window === "undefined") return;
    if (window.GD_SLS_RUM) {
      if (typeof err === "string") {
        err = new Error(err);
      }

      if (customLog) {
        err.log = customLog;
      }

      window.GD_SLS_RUM.addError(err);
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * 前端自定义日志上报
 * @param {Record<string, any>} log
 * @returns
 */
export function gdSlsLog(log) {
  try {
    if (typeof window === "undefined") return;
    if (window.GD_SLS_RUM && log) {
      window.GD_SLS_RUM.addLog(log);
    }
  } catch (error) {
    console.error(error);
  }
}
